/**
 * EvidencePanel.tsx
 *
 * Displays evidence linked to ontology edges and rules
 * Shows which evidence items satisfy each FAIR requirement
 */

import React from 'react';

type OntologyEdge = {
  source: string;
  target: string;
  label: string;
  kind: string;
  pillar?: 'F' | 'A' | 'I' | 'R';
  status?: 'satisfied' | 'missing' | 'unknown' | 'not_applicable';
  satisfiedBy?: string[];
};

interface EvidencePanelProps {
  edge: OntologyEdge;
  onClose: () => void;
}

export function EvidencePanel({ edge, onClose }: EvidencePanelProps) {
  const isSatisfied = edge.status === 'satisfied';
  const isNA = edge.status === 'not_applicable';
  const evidenceIds = edge.satisfiedBy || [];

  // Determine header background color based on status
  const headerBg = isNA ? '#eef2ff' : isSatisfied ? '#f0fdf4' : '#fef2f2';

  return (
    <div className="evidence-panel" style={{
      position: 'fixed',
      right: '20px',
      bottom: '20px',
      width: '420px',
      maxHeight: '520px',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      zIndex: 1000
    }}>
      <div className="panel-header" style={{
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: headerBg,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
            {edge.label}
          </h3>
          <p style={{ fontSize: '12px', color: '#6b7280' }}>
            {edge.source} → {edge.target}
          </p>
        </div>

        <button
          className="btn-close"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="panel-body" style={{
        padding: '16px',
        maxHeight: '400px',
        overflowY: 'auto'
      }}>
        {/* Status badge */}
        <div className="status-badge" style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          marginBottom: '16px',
          backgroundColor: isNA ? '#6366f1' : isSatisfied ? '#22c55e' : '#ef4444',
          color: '#ffffff'
        }}>
          {isNA ? '— Not Applicable' : isSatisfied ? '✓ Satisfied' : '✗ Missing'}
        </div>

        {/* N/A explanation */}
        {isNA && (
          <div style={{
            padding: '16px',
            backgroundColor: '#eef2ff',
            borderRadius: '6px',
            borderLeft: '4px solid #6366f1',
            marginBottom: '16px',
          }}>
            <p style={{ fontSize: '13px', color: '#4338ca', lineHeight: '1.6', margin: 0 }}>
              {getEvidenceDescription(evidenceIds[0] || edge.label.split(':')[0]?.trim() || '')}
            </p>
            <p style={{ fontSize: '12px', color: '#6366f1', marginTop: '8px', marginBottom: 0 }}>
              This rule is excluded from the FAIR score denominator. The corresponding SHACL shape is deactivated.
            </p>
          </div>
        )}

        {/* Satisfied evidence */}
        {isSatisfied && evidenceIds.length > 0 ? (
          <div className="evidence-list">
            <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
              Supporting Evidence ({evidenceIds.length})
            </h4>

            {evidenceIds.map((id, index) => (
              <div
                key={id}
                className="evidence-item"
                style={{
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  border: '1px solid #e5e7eb'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Evidence #{index + 1}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    fontFamily: 'monospace'
                  }}>
                    {id}
                  </span>
                </div>

                <p style={{
                  fontSize: '13px',
                  color: '#4b5563',
                  lineHeight: '1.5'
                }}>
                  {getEvidenceDescription(id)}
                </p>
              </div>
            ))}
          </div>
        ) : !isNA ? (
          <div className="no-evidence" style={{
            padding: '24px',
            textAlign: 'center',
            color: '#9ca3af'
          }}>
            <p style={{ fontSize: '14px' }}>
              This requirement is not satisfied by the current analysis.
            </p>
            <p style={{ fontSize: '12px', marginTop: '8px' }}>
              {getEvidenceDescription(evidenceIds[0] || edge.label.split(':')[0]?.trim() || '')}
            </p>
          </div>
        ) : null}

        {edge.pillar && (
          <div className="pillar-info" style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#f0f9ff',
            borderRadius: '6px',
            borderLeft: `4px solid ${getPillarColor(edge.pillar)}`
          }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
              FAIR Pillar:
            </span>
            {' '}
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              {edge.pillar} - {getPillarName(edge.pillar)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Canonical FAIR rule evidence descriptions for Databricks assessments */
const FAIR_EVIDENCE_DESCRIPTIONS: Record<string, string> = {
  'F1': 'Dataset has a persistent identifier (Unity Catalog fully-qualified name). Validated by SHACL Shape_F1.',
  'F2': 'Dataset is described with rich metadata (description, column descriptions, tags, owner). Validated by SHACL Shape_F2.',
  'F3': 'Metadata explicitly includes the dataset identifier. Validated by SHACL Shape_F3.',
  'F4': 'Dataset is registered in Unity Catalog, which is a searchable resource with REST API. Evaluated via metadata.',
  'A1': 'Dataset is retrievable via its identifier using HTTPS (standardized protocol). Validated by SHACL Shape_A1.',
  'A1.1': 'HTTPS is an open, free, and universally implementable protocol. Evaluated via metadata.',
  'A1.2': 'Databricks supports OAuth2 M2M and Personal Access Token (PAT) authentication. Evaluated via metadata.',
  'A2': 'Unity Catalog does not expose metadata persistence policies. SHACL Shape_A2 is deactivated.',
  'I1': 'Typed columns in Unity Catalog constitute formal, machine-readable data representation. Evaluated via metadata.',
  'I2': 'Dataset uses FAIR vocabularies with persistent URIs. Validated by SHACL Shape_I2.',
  'I3': 'Unity Catalog does not support qualified cross-table references. SHACL Shape_I3 is deactivated.',
  'I4': 'Dataset meets domain-relevant community standards. Validated by SHACL Shape_I4.',
  'R1': 'Dataset is described with accurate and relevant attributes (>=5 properties). Validated by SHACL Shape_R1.',
  'R1.1': 'Dataset has a clear and accessible data usage license. Validated by SHACL Shape_R2.',
  'R1.2': 'Unity Catalog metadata does not include detailed provenance records. SHACL Shape_R3 is deactivated.',
  'R1.3': 'Unity Catalog metadata does not declare domain-specific community standard conformance. SHACL Shape_R4 is deactivated.',
};

/**
 * Get evidence description from a FAIR rule ID or legacy evidence ID
 */
function getEvidenceDescription(evidenceId: string): string {
  // Check canonical FAIR rule ID first
  if (FAIR_EVIDENCE_DESCRIPTIONS[evidenceId]) {
    return FAIR_EVIDENCE_DESCRIPTIONS[evidenceId];
  }

  // Legacy fallback for old check:N format
  if (evidenceId.includes('pid')) {
    return 'Dataset has a persistent identifier (DOI or similar)';
  }
  if (evidenceId.includes('license')) {
    return 'License information found in metadata';
  }
  if (evidenceId.includes('format')) {
    return 'Data format is standard and documented';
  }
  if (evidenceId.includes('rule:')) {
    const ruleId = evidenceId.split('rule:')[1].split(',')[0];
    return `Satisfied by FAIR rule ${ruleId}`;
  }
  if (evidenceId.startsWith('check:')) {
    return `Legacy check #${evidenceId.split(':')[1]} from previous analysis`;
  }

  return 'Evidence detected in analysis';
}

/**
 * Get pillar full name
 */
function getPillarName(pillar: 'F' | 'A' | 'I' | 'R'): string {
  const names = {
    F: 'Findable',
    A: 'Accessible',
    I: 'Interoperable',
    R: 'Reusable'
  };
  return names[pillar];
}

/**
 * Get pillar color
 */
function getPillarColor(pillar: 'F' | 'A' | 'I' | 'R'): string {
  const colors = {
    F: '#3b82f6', // blue
    A: '#22c55e', // green
    I: '#f59e0b', // amber
    R: '#ec4899'  // pink
  };
  return colors[pillar];
}
